import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ITaxDashboardByStoreItem, ITaxDashboardByUFItem } from '@/@types/tax-dashboard';
import { situacaoMap } from './constants';

export interface IUFTableData {
  UF: string;
  Sucesso: number;
  Contingência: number;
  Pendente: number;
  Cancelado: number;
  Total: number;
}

export interface IStoreTableData {
  STORE: string;
  Sucesso: number;
  Contingência: number;
  Pendente: number;
  Cancelado: number;
  Total: number;
}

export const transformData = (data: any[]) => {
  // Primeiro, ordenar os dados por data
  const sortedData = [...data].sort((a, b) => new Date(a.DATA).getTime() - new Date(b.DATA).getTime());

  // Criar um mapa de todas as datas únicas
  const uniqueDates = Array.from(new Set(sortedData.map(item => item.DATA)));

  // Para cada data, criar um objeto com todos os status inicializados como 0
  return uniqueDates.map(date => {
    const baseObj: any = {
      date: format(parseISO(date), 'dd/MM/yyyy', { locale: ptBR }),
      Sucesso: 0,
      Contingência: 0,
      Pendente: 0,
      Cancelado: 0,
    };

    // Somar as quantidades para cada status nesta data
    const itemsForDate = sortedData.filter(item => item.DATA === date);
    itemsForDate.forEach(item => {
      const situacao = item.SITUACAO === null ? "Pendente" : situacaoMap[item.SITUACAO] || "Pendente";
      baseObj[situacao] += item.QTDE;
    });

    return baseObj;
  });
};

export const transformDataByUF = (data: ITaxDashboardByUFItem[]): IUFTableData[] => {
  const ufMap = new Map<string, IUFTableData>();

  // Inicializar o mapa com todas as UFs
  data.forEach(item => {
    if (!ufMap.has(item.UF)) {
      const initialData: IUFTableData = {
        UF: item.UF,
        Sucesso: 0,
        Contingência: 0,
        Pendente: 0,
        Cancelado: 0,
        Total: 0,
      };
      ufMap.set(item.UF, initialData);
    }
  });

  // Preencher as quantidades
  data.forEach(item => {
    const situacao = situacaoMap[item.SITUACAO] || "Pendente";
    const ufData = ufMap.get(item.UF)!;
    ufData[situacao as keyof Omit<IUFTableData, 'UF' | 'Total'>] = (ufData[situacao as keyof Omit<IUFTableData, 'UF' | 'Total'>] || 0) + item.QTDE;
    ufData.Total += item.QTDE;
  });

  // Converter o mapa em array e ordenar por total (decrescente) e depois por UF
  return Array.from(ufMap.values()).sort((a, b) => {
    const totalDiff = b.Total - a.Total;
    return totalDiff !== 0 ? totalDiff : a.UF.localeCompare(b.UF);
  });
};

export const transformDataByStore = (data: ITaxDashboardByStoreItem[]): IStoreTableData[] => {
  const storeMap = new Map<string, IStoreTableData>();

  // Inicializar o mapa com todas as lojas
  data.forEach(item => {
    if (!storeMap.has(item.LOJA)) {
      const initialData: IStoreTableData = {
        STORE: item.LOJA,
        Sucesso: 0,
        Contingência: 0,
        Pendente: 0,
        Cancelado: 0,
        Total: 0,
      };
      storeMap.set(item.LOJA, initialData);
    }
  });

  // Preencher as quantidades
  data.forEach(item => {
    const situacao = situacaoMap[item.SITUACAO] || "Pendente";
    const storeData = storeMap.get(item.LOJA)!;
    storeData[situacao as keyof Omit<IStoreTableData, 'STORE' | 'Total'>] = (storeData[situacao as keyof Omit<IStoreTableData, 'STORE' | 'Total'>] || 0) + item.QTDE;
    storeData.Total += item.QTDE;
  });

  // Converter o mapa em array e ordenar por total (decrescente) e depois por loja
  return Array.from(storeMap.values()).sort((a, b) => {
    const totalDiff = b.Total - a.Total;
    return totalDiff !== 0 ? totalDiff : String(a.STORE).localeCompare(String(b.STORE), 'pt-BR');
  });
};